import { get, map, set, reduce, update, filter, chain, compact, pick, isEmpty } from "lodash"
import { FORMATS, makeCurrencyFormatter, getCurrencySymbol, makeCustomFormatter } from "helpers/formatters/numbers"
import { Factor } from "components/factor-view"

const EMPTY_ARRAY = []
const EMPTY_OBJECT = {}

// The minimum annualized volatility for aggregating and displaying IR values (small AV values result in very large IR)
export const ANNUALIZED_VOLATILITY_THRESHOLD = 0.001

export const numberFormatter = makeCustomFormatter(FORMATS.NUMBER_FORMAT, { emptyValueFiller: "-" })
export const percentageFormatter = (number) => numberFormatter(number * 100)

export function computeSummary(summary, varSummary, key) {
  return {
    id: key,
    risk: get(summary, ["attribution", "summary", key]),
    valueAtRisk: get(varSummary, ["attribution", "summary", key]),
  }
}

export function computeSummaryRows(summary, varSummary) {
  return [
    computeSummary(summary, varSummary, "specific"),
    computeSummary(summary, varSummary, "factors"),
  ]
}

// We want to avoid displaying factors which only have nil values [risk, currencyExposure, valueAtRisk]
// exposure factor is an object { currencyExposure, net }
function makeFactorHasValues(risk, exposure, valueAtRisk) {
  return (factorId) => get(risk, [factorId, "value"]) || get(exposure, [factorId, "currencyExposure"]) || get(valueAtRisk, factorId)
}

// We remove factors that have nil values for all [risk, currencyExposure, valueAtRisk]
export function computeFactorsWithMetada(risk, exposure, valueAtRisk, rawFactors, factorVolatility) {
  const factorHasValues = makeFactorHasValues(risk, exposure, valueAtRisk)
  return reduce(rawFactors, (factors, rawFactor, factorId) => factorHasValues(factorId)
    ? set(factors, factorId, {
      id: factorId,
      label: rawFactor.alias || rawFactor.label,
      category: get(rawFactors, [factorId, "category"]),
      risk: get(risk, [factorId, "value"]),
      currencyExposure: get(exposure, [factorId, "currencyExposure"]),
      valueAtRisk: get(valueAtRisk, factorId),
      ...(!isEmpty(factorVolatility) && {
        exposure: get(exposure, [factorId, "net"]),
        valueAtRiskPct: get(factorVolatility, factorId) * get(exposure, [factorId, "net"]),
      }),
    }) : factors, {})
}

// A category containing only nil factors will get removed by the filter on category.risk since the value will be nil
export function computeFactorViewCategories(categories, summaryRows, factorCategories) {
  return [
    ...summaryRows,
    ...chain(categories)
      .map((category) => ({
        id: category.id,
        ...get(factorCategories, category.id, {}),
      }))
      .filter(category => category.risk || category.valueAtRisk)
      .value(),

  ]
}

export function computeFactorViewRisk(factors) {
  return reduce(factors, (categories, factor) => update(categories, factor.category, existing => ({
    risk: get(existing, "risk", 0) + get(factor, "risk", 0),
    // We don't currently support VaR for factor categories
    valueAtRisk: null,
  })), {})
}

export function computeFactorVaR(valueAtRisk) {
  return reduce(valueAtRisk, (factors, factor) => set(factors, factor.id, factor.value), {})
}

export function computeTotalRow(summary, varSummary, isComposite, currency) {
  return compact([
    "Total",
    percentageFormatter(get(summary, "total")),
    !isComposite && makeCurrencyFormatter(currency, { displayCurrencySymbol: false })(get(varSummary, "total")),
  ])
}

export function computeFactorExposure(factors) {
  return reduce(factors, (factorExposures, factor) => ({
    ...factorExposures,
    [factor.id]: pick(factor, ["currencyExposure", "net"]),
  }), {})
}

export function computeCurrentFactors(factors, selectedCategory) {
  return selectedCategory === "factors" ? map(factors) : filter(factors, factor => factor.category === selectedCategory)
}

export function makeSelectors(stateObjectKey, { intraday } = { intraday: false }) {
  const simulationKey = !intraday ? "simulation" : "intradaySimulation"

  return [
    state => get(state, `global.data.${stateObjectKey}.data.model.${simulationKey}.risk.0`, EMPTY_OBJECT),
    state => get(state, `global.data.${stateObjectKey}.data.model.${simulationKey}.valueAtRisk.0`, EMPTY_OBJECT),
    state => get(state, `global.data.${stateObjectKey}.data.model.${simulationKey}.risk.0.attribution.factors`, EMPTY_ARRAY),
    state => get(state, `global.data.${stateObjectKey}.data.model.${simulationKey}.valueAtRisk.0.attribution.factors`, EMPTY_ARRAY),
    state => get(state, `global.data.${stateObjectKey}.data.model.${simulationKey}.exposure.0.factors`, EMPTY_ARRAY),
    state => get(state, `modules.${stateObjectKey}.selectedFactorCategory`),
  ]
}

export function getFactorRiskColumns(type, isComposite, currency) {
  const dollarFormatter = makeCurrencyFormatter(currency, { displayCurrencySymbol: false })
  const currencySymbol = getCurrencySymbol(currency)

  // Available columns
  // factor, risk, currencyExposure, valueAtRisk, valueAtRiskPct, exposure
  const factor = {
    id: "label",
    name: "Factor",
    render: factor => <Factor factor={factor} />,
  }

  const risk = (name) => ({
    id: "risk",
    name,
    formatter: percentageFormatter,
    numeric: true,
    defaultSort: "desc",
  })

  const valueAtRisk = (name) => ({
    id: "valueAtRisk",
    name,
    formatter: dollarFormatter,
    numeric: true,
  })

  const valueAtRiskPct = {
    id: "valueAtRiskPct",
    name: "Standalone VaR\u00A0(%)",
    formatter: percentageFormatter,
    numeric: true,
  }

  const currencyExposure = {
    id: "currencyExposure",
    name: `${currency} exposure`,
    formatter: dollarFormatter,
    numeric: true,
  }

  const exposure = {
    id: "exposure",
    name: "Exposure",
    formatter: numberFormatter,
    numeric: true,
  }

  switch (true) {
    // Total
    case type === "total" && !isComposite:
      return [risk("% Predicted risk"), valueAtRisk(`Predicted risk (${currencySymbol})`)]
    case type === "total" && isComposite:
      return [risk("% Predicted risk")]
    // Categories
    case type === "categories" && !isComposite:
      return [risk("% Risk decomp"), valueAtRisk(`Predicted risk (${currencySymbol})`)]
    case type === "categories" && isComposite:
      return [risk("% Risk decomp")]
    // Table
    case type === "table" && !isComposite:
      return [factor, risk("% Risk decomp"), valueAtRisk(`Standalone VaR\u00A0(${currencySymbol})`), currencyExposure]
    case type === "table" && isComposite:
      return [factor, risk("% Risk decomp"), valueAtRiskPct, exposure]
  }
}
